import { useEffect, useState } from 'react'

import { Link } from '@twistopayments/design-system/components/atoms/link'

import type { SectionsListComponent } from '@twisto/api-cms'
import { useBreakpoint } from '@twisto/hooks'

import { useHighlightedText } from 'homepage/libs/hooks/use-highlighted'

import { AccessibleDiv, StrapiMedia } from '../../atoms'

import * as styles from './list.styles'

const IMG_CONTAINER_ID_DESKTOP = 'img-container-desc'
const IMG_CONTAINER_ID_MOBILE = 'img-container-mob'
const IMG_ID = 'vert-slide'
const IMG_ID_MOBILE = 'vert-slide-mob'

export function List(props: SectionsListComponent) {
  const { title, titleHighlightPart, listItemVertical, autoSlideTime } = props
  const [active, setActive] = useState(0)
  const [interacted, setInteracted] = useState(false)
  const isDesktop = useBreakpoint('mdUp')

  // seconds
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const timeToNextSlide = autoSlideTime ? autoSlideTime * 1000 : null

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (isDesktop) {
      const img = document.querySelector(`#${IMG_ID}-${active}`)
      const container = document.querySelector(`#${IMG_CONTAINER_ID_DESKTOP}`)

      if (container && img) {
        container.scrollTop = img.clientHeight * active
      }
    } else {
      const img = document.querySelector(`#${IMG_ID_MOBILE}-${active}`)
      const container = document.querySelector(`#${IMG_CONTAINER_ID_MOBILE}`)

      if (container && img) {
        container.scrollLeft = img.clientWidth * active
      }
    }
  }, [active, isDesktop])

  useEffect(() => {
    if (
      !listItemVertical?.length ||
      listItemVertical.length <= 1 ||
      interacted ||
      !timeToNextSlide
    ) {
      return
    }

    const move = () => {
      setActive((index) => (index + 1) % listItemVertical.length)
    }

    const timer = setInterval(move, timeToNextSlide)

    return () => clearInterval(timer)
  }, [listItemVertical, interacted, timeToNextSlide])

  const {
    before: titleBefore,
    highlight: titleHighlight,
    after: titleAfter,
  } = useHighlightedText(title, titleHighlightPart)

  const selectItem = (i: number) => {
    setActive(i)
    setInteracted(true)
  }

  return (
    <div css={styles.wrap}>
      <div css={styles.title}>
        {titleBefore}
        <span>{titleHighlight}</span>
        {titleAfter}
      </div>

      <div css={styles.content}>
        {/* images */}
        <div css={styles.imageContainer} id={IMG_CONTAINER_ID_DESKTOP}>
          {listItemVertical?.map((item, i) => (
            <div key={item.id} css={styles.img}>
              <StrapiMedia
                {...item.src}
                css={styles.img}
                id={`${IMG_ID}-${i}`}
              />
            </div>
          ))}
        </div>

        {/* cards */}
        <ul css={styles.list} id={IMG_CONTAINER_ID_MOBILE}>
          {listItemVertical?.map((item, i) => (
            <AccessibleDiv
              key={item.id}
              as="li"
              css={[styles.card, active === i && styles.cardActive]}
              role="button"
              onActivate={() => selectItem(i)}
              onTouchStart={() => selectItem(i)}
            >
              <StrapiMedia
                {...item.src}
                key={item.id}
                css={[styles.img, styles.cardImg]}
                id={`${IMG_ID_MOBILE}-${i}`}
              />
              <div css={styles.cardTitle}>{item.title}</div>

              <div css={styles.cardDesc}>{item.description}</div>

              {item.cta?.text && item.cta.url && (
                <Link css={styles.cardLink} to={item.cta.url}>
                  {item.cta.text}
                </Link>
              )}
            </AccessibleDiv>
          ))}
        </ul>
      </div>
    </div>
  )
}
